<template>
	<div
		v-if="!allDone"
		class="onboarding-hero"
		data-test="onboarding-hero"
	>
		<div class="onboarding-hero__intro">
			<div class="onboarding-hero__intro-text">
				<div class="onboarding-hero__intro-heading">
					<div
						class="onboarding-hero__eyebrow sgnl-caption-medium"
						data-test="onboarding-hero-progress"
					>
						Getting started · {{ doneCount }}/{{ steps.length }} complete
					</div>
					<h2 class="onboarding-hero__title sgnl-h6-medium">
						Finish setting up your Signaloid Cloud workspace
					</h2>
				</div>
				<p class="onboarding-hero__body sgnl-paragraph-s-regular">
					A few quick steps run and preview your first task, set up custom cores, connect repositories, and
					set up Compute Engine API access.
				</p>
			</div>
			<div
				class="onboarding-hero__progress"
				role="progressbar"
				aria-label="Onboarding progress"
				:aria-valuenow="doneCount"
				:aria-valuemax="steps.length"
			>
				<div
					class="onboarding-hero__progress-fill"
					:style="{ width: progressPercent + '%' }"
				/>
			</div>
		</div>
		<div class="onboarding-hero__steps">
			<button
				v-for="step in steps"
				:key="step.id"
				type="button"
				class="onboarding-hero__step"
				:class="{ 'onboarding-hero__step--done': step.done }"
				:data-test="`onboarding-hero-step-${step.id}`"
				@click="onStepClick(step)"
			>
				<span class="onboarding-hero__step-checkbox">
					<Transition name="check-pop">
						<Checkmark16 v-if="step.done" />
					</Transition>
				</span>
				<span class="onboarding-hero__step-label sgnl-paragraph-s-regular">{{ step.label }}</span>
				<ChevronRight16
					v-if="!step.done"
					class="onboarding-hero__step-chevron"
				/>
			</button>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";
import ChevronRight16 from "@carbon/icons-vue/es/chevron--right/16";

export type OnboardingStep = {
	id: "apiKey" | "customCore" | "firstTask" | "repository";
	label: string;
	done: boolean;
	to: string;
};

export default defineComponent({
	name: "OnboardingHero",
	components: { Checkmark16, ChevronRight16 },
	props: {
		steps: {
			type: Array as PropType<OnboardingStep[]>,
			required: true,
		},
	},
	emits: ["step-click"],
	computed: {
		doneCount(): number {
			return this.steps.filter((s) => s.done).length;
		},
		allDone(): boolean {
			return this.doneCount >= this.steps.length;
		},
		progressPercent(): number {
			return (this.doneCount / Math.max(1, this.steps.length)) * 100;
		},
	},
	methods: {
		onStepClick(step: OnboardingStep) {
			this.$posthog?.capture("home_onboarding_step_clicked", { step: step.id, done: step.done });
			this.$emit("step-click", step);
			this.$router.push(step.to);
		},
	},
});
</script>

<style lang="scss" scoped>
.onboarding-hero {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--sgnl-space-24);
	padding: var(--sgnl-space-20) var(--sgnl-space-28);
	background: var(--base-b-black);
	color: var(--base-b-white);
	border-radius: var(--sgnl-border-radius-6);
}

.onboarding-hero__intro {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-16);
}

.onboarding-hero__intro-text {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.onboarding-hero__intro-heading {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-6);
}

.onboarding-hero__eyebrow {
	color: var(--neutral-b-gray-400);
	text-transform: uppercase;
	letter-spacing: var(--sgnl-font-letter-spacing-055);
}

.onboarding-hero__title {
	margin: 0;
	color: var(--base-b-white);
}

.onboarding-hero__body {
	margin: 0;
	color: var(--neutral-b-gray-200);
	max-width: var(--sgnl-container-560);
}

.onboarding-hero__progress {
	width: var(--sgnl-unit-256);
	max-width: 100%;
	height: var(--sgnl-unit-4);
	background: var(--opacity-white-b-white-8);
	border-radius: var(--sgnl-border-radius-6);
	overflow: hidden;
}

.onboarding-hero__progress-fill {
	height: 100%;
	background: var(--sgnl-brand-background);
	transition: width var(--sgnl-motion-duration-slow-01) var(--sgnl-expressive-effect-entrance);
}

.onboarding-hero__steps {
	width: var(--sgnl-column-grid-3);
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.onboarding-hero__step {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-10);
	padding: var(--sgnl-space-8) var(--sgnl-space-10);
	border-radius: var(--sgnl-border-radius-6);
	background: var(--opacity-white-b-white-8);
	border: var(--sgnl-border-width-1) solid var(--opacity-white-b-white-8);
	font-family: inherit;
	cursor: pointer;
	text-align: left;
	color: var(--base-b-white);
	transition:
		transform var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard),
		background var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard),
		box-shadow var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard);
}

.onboarding-hero__step:hover {
	background: var(--opacity-white-b-white-12);
	box-shadow: 0 0 0 var(--sgnl-border-width-2) var(--sgnl-focus-ring-secondary);
}

.onboarding-hero__step:active {
	transform: scale(0.985);
}

.onboarding-hero__step:hover:not(.onboarding-hero__step--done) .onboarding-hero__step-label {
	text-decoration: underline;
}

.onboarding-hero__step--done {
	background: transparent;
	border-color: transparent;
}

.onboarding-hero__step-checkbox {
	width: var(--sgnl-icon-16);
	height: var(--sgnl-icon-16);
	border-radius: 50%;
	display: grid;
	place-items: center;
	border: var(--sgnl-border-width-1) solid var(--neutral-b-gray-900);
	flex-shrink: 0;
	color: var(--base-b-white);
	transition:
		background var(--sgnl-motion-duration-moderate-01) var(--sgnl-expressive-effect-entrance),
		border-color var(--sgnl-motion-duration-moderate-01) var(--sgnl-expressive-effect-entrance);
}

.onboarding-hero__step--done .onboarding-hero__step-checkbox {
	background: var(--sgnl-brand-background);
	border-color: transparent;
}

.check-pop-enter-active {
	transition:
		transform var(--sgnl-motion-duration-moderate-02) var(--sgnl-expressive-effect-entrance),
		opacity var(--sgnl-motion-duration-moderate-01) var(--sgnl-expressive-effect-entrance);
}
.check-pop-enter-from {
	transform: scale(0.6);
	opacity: 0;
}

.onboarding-hero__step-label {
	flex: 1;
	min-width: 0;
}

.onboarding-hero__step--done .onboarding-hero__step-label {
	color: var(--neutral-b-gray-400);
	text-decoration: line-through;
}

.onboarding-hero__step-chevron {
	color: var(--neutral-b-gray-400);
	flex-shrink: 0;
}

@media (max-width: 1099px) {
	.onboarding-hero {
		flex-direction: column;
		align-items: flex-start;
	}
	.onboarding-hero__steps {
		width: 100%;
	}
}
</style>
